import NotFound from '@/pages/404';
import { IMenu, IBreadcrumb } from '@/typings';
import { isNotEmpty } from '@qsxy/element-plus-react';
import omit from 'lodash/omit';
import uniqBy from 'lodash/uniqBy';

/**
 * 平铺菜单
 * @param menuList
 * @param result
 * @param parentId
 */
export const flatMenu = (menuList: IMenu[], result: IMenu[] = [], parentId = null): IMenu[] => {
	menuList.forEach(item => {
		result.push({
			...omit(item, 'children'),
			parentId,
		});
		if (item?.children) {
			flatMenu(item.children, result, item.id);
		}
	});
	return result;
};

/**
 * 寻找菜单上级
 * @param menuList
 * @param id
 * @param result
 */
const findMenuParent = (menuList: IMenu[], id: string, result: IMenu[] = []) => {
	const cur = menuList.find(item => item.id === id);
	if (cur) {
		result.push(cur);
		if (cur.parentId !== null) {
			findMenuParent(menuList, cur.parentId, result);
		}
	}
};

/**
 * 根据url获取菜单
 * @param menuUrl
 * @returns
 */
export const findMenuByUrl = (menuList: IMenu[], menuUrl: string): IMenu[] => {
	const menus: IMenu[] = flatMenu(menuList);
	let result: IMenu[] = [];

	menuUrl.split('/').forEach((url: string, i: number) => {
		if (i === 0) {
			const menu = menus.find(item => item.url === url || item?.url?.split('/').includes(url));
			if (menu) {
				result.push(menu);
				if (menu.parentId) {
					findMenuParent(menus, menu.parentId, result);
				}
			}
			result = result.reverse();
		} else {
			result.push(menus.find(item => item.url === url || item?.url?.split('/').includes(url)));
		}
	});

	result = result.filter(item => !!item);
	result = uniqBy(result, 'id');
	return result;
};

export const findRouteByUrl = (routes: IMenu[], menuUrl: string): IMenu => {
	if (isNotEmpty(menuUrl)) {
		menuUrl = menuUrl.startsWith('/') ? menuUrl : `/${menuUrl}`;
	}
	return routes.find(item => isNotEmpty(item.id) && isNotEmpty(menuUrl) && (item.id.startsWith('/') ? item.id : `/${item.id}`) === menuUrl);
};

// /**
//  * 根据url获取菜单路径
//  * @param url
//  * @returns
//  */
// export const findMenuPath = (menuList: IMenu[], url: string) => {
//     const result: IMenu[] = findMenuByUrl(menuList, url);
//     return result.map(item => item.id);
// };

/**
 * 根据菜单路径获取面包屑
 * @param menuId
 * @returns
 */
export function getBreadByMenuPath(menuList: IMenu[], menuIds: string[]): IBreadcrumb[] {
	const menus = flatMenu(menuList);
	let result: IMenu[] = [];

	menuIds.forEach((id: string, i: number) => {
		if (i === 0) {
			const menu = menus.find(item => item.id === id);
			if (menu) {
				result.push(menu);
				if (menu.parentId) {
					findMenuParent(menus, menu.parentId, result);
				}
			}
			result = result.reverse();
		} else {
			result.push(menus.find(item => item.id === id));
		}
	});

	return result
		.filter(item => !!item)
		.map((menuItem, i) => {
			return {
				id: menuItem.id,
				isLink: i < menuIds.length - 1 && isNotEmpty(menuItem?.url) && menuItem?.children?.length === 0 && menuItem.isLink !== false,
				title: menuItem?.name,
				url: menuItem?.url,
			};
		});
}

/**
 * 根据url获取面包屑
 * @param menuPath
 * @returns
 */
export function findBreadByUrl(menuList: IMenu[], menuPath: string): IBreadcrumb[] {
	const result = findMenuByUrl(menuList, menuPath);
	return result
		.filter(item => !!item)
		.map((menuItem, i) => {
			return {
				id: menuItem.id,
				isLink: i < result.length - 1 && isNotEmpty(menuItem?.url),
				title: menuItem?.name,
				url: menuItem?.url,
			};
		});
}

export function mergeMenu(menuList: Omit<IMenu, 'component'>[], routes: IMenu[]): IMenu[] {
	return menuList
		.map(menu => {
			const { url, children = [] } = menu;
			const matchRoute = findRouteByUrl(routes, url);
			if (matchRoute) {
				const { id, hidden } = matchRoute;
				if (children.length > 0) {
					const _children = mergeMenu(children, routes);
					if (_children.length > 0) {
						return { ...menu, id, menuId: menu.id, hidden, children: _children };
					} else {
						return { ...menu, menuId: menu.id, id, hidden, hasChild: false, children: [] };
					}
				} else {
					return { ...menu, menuId: menu.id, id, hidden };
				}
			}
			return menu;
		})
		.filter(item => !item.hidden)
		.sort((a, b) => a.sort - b.sort);
}

export function mergeRoutes(menuList: Omit<IMenu, 'component'>[], routes: IMenu[], result: IMenu[] = []): IMenu[] {
	menuList.forEach(menu => {
		const { url, children = [] } = menu;
		const matchRoute = findRouteByUrl(routes, url);
		if (matchRoute) {
			const { id, component, hidden } = matchRoute;
			result.push({
				...omit(menu, 'children'),
				id,
				component,
				hidden,
			});
		} else {
			result.push({
				...omit(menu, 'children'),
				component: NotFound,
			});
		}
		if (children.length > 0) {
			mergeRoutes(children, routes, result);
		}
	});
	return result;
}

export const includesMenu = (openMenu: string, menuPath: string) => {
	if (openMenu && menuPath) {
		return openMenu
			.split('/')
			.filter(item => !!item)
			.join('/')
			.replace(menuPath, '')
			.startsWith('/');
	}
	return false;
};
